// TODO (Noah, 2024-10-09): Re-enable this rule
/* eslint-disable replo/consistent-component-exports */
import type { MenuItem as MenuItemProps } from "@v1/components/designSystem/menu/Menu";
import type {
  ReploElementMetadata,
  ReploElementType,
} from "schemas/generated/element";
import type { Folder } from "schemas/generated/folder";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import {
  Group,
  GroupHeader,
  GroupTitle,
  GroupTitleContainer,
} from "@v1/components/common/designSystem/Group";
import Input from "@v1/components/common/designSystem/Input";
import { toast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Menu, MenuTrigger } from "@v1/components/designSystem/menu/Menu";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import Popover from "@v1/components/designSystem/popover/Popover";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import { useCreateElement } from "@v1/hooks/element/useCreateElement";
import { useDuplicateElement } from "@v1/hooks/element/useDuplicateElement";
import { useElementName } from "@v1/hooks/element/useElementName";
import useListAllElementsMetadata from "@v1/hooks/element/useListAllElementsMetadata";
import { useArchiveElement } from "@v1/hooks/useArchiveElement";
import { useDefaultCollapsedElementTypeGroups } from "@v1/hooks/useDefaultCollapsedElementTypeGroups";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import {
  selectDraftElementId,
  selectDraftElementType,
  selectIsShopifyIntegrationEnabled,
} from "@v1/reducers/core-reducer";
import {
  setIsRightBarAnalyticsOpen,
  setLeftBarActiveTab,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { folderRouter, folderUtils } from "@v1/trpc/routes/folder";
import {
  canRenameElement,
  REPLO_ELEMENT_DEFAULT_FOLDER_NAME,
} from "@v1/utils/element";
import { saveLastSelectedElementIdForTypeForStore } from "@v1/utils/localStorage";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useOpenModal } from "@v2/store/modalStore";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { docs } from "@v2/utils/docs";
import { routes } from "@v2/utils/router";
import { formatDistanceToNowStrict } from "date-fns";
import orderBy from "lodash-es/orderBy";
import size from "lodash-es/size";
import {
  ChartLine,
  ChevronDown,
  Ellipsis,
  File,
  Folder as FolderIcon,
  House,
  LayoutTemplate,
  ListFilter,
  Plus,
  Search,
} from "lucide-react";
import {
  generatePath,
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { animated, config, useSpring } from "react-spring";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { isEmpty } from "replo-utils/lib/misc";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { reploElementTypeSchema } from "schemas/element";
import { z } from "zod";

type FolderWithElements = {
  folder?: Folder;
  elements: ReploElementMetadata[];
};

type FolderElementMetadataMapping = Record<Folder["id"], FolderWithElements>;

type GroupedFoldersWithElementMetadata = Record<
  ReploElementType,
  FolderElementMetadataMapping
>;

type ElementListProps = {
  folderElementMetadataMapping: FolderElementMetadataMapping;
  onClick: (contentItem: ReploElementMetadata) => void;
  currentElementType: ReploElementType;
};

type ElementTypeGroupInfo = {
  type: ReploElementType;
  title: string;
  createTooltipText: string;
  createButtonDataTestId: string;
  groupDataTestId: string;
};

type ElementTypeGroupContentProps = {
  folderElementMetadataMapping: FolderElementMetadataMapping;
  elementTypeGroup: ElementTypeGroupInfo;
  resetElement: (elementId: string) => void;
  isCollapsed: boolean;
  setCollapsedElementTypeGroups: (elementType: ReploElementType | null) => void;
  groupDataTestId: string;
};

type SortMenuProps = {
  sortBy: SortBy;
  setSortBy: (value: SortBy) => void;
  sortOrder: SortOrder;
  setSortOrder: (value: SortOrder) => void;
  showFirst: ShowFirst[];
  setShowFirst: (value: ShowFirst, checked: boolean) => void;
};

const ELEMENT_TYPE_GROUP_LIST: Array<ElementTypeGroupInfo> = [
  {
    title: "Pages",
    type: "page",
    createTooltipText: "New Page",
    createButtonDataTestId: "add-page",
    groupDataTestId: "pages-group",
  },
  {
    title: "Product Templates",
    type: "shopifyProductTemplate",
    createTooltipText: "New Product Template",
    createButtonDataTestId: "add-product-template",
    groupDataTestId: "product-templates-group",
  },
  {
    title: "Sections",
    type: "shopifySection",
    createTooltipText: "New Shopify Section",
    createButtonDataTestId: "add-section",
    groupDataTestId: "sections-group",
  },
  {
    title: "Blog Posts",
    type: "shopifyArticle",
    createTooltipText: "New Blog Post",
    createButtonDataTestId: "add-article",
    groupDataTestId: "blog-posts-group",
  },
];

type SortBy = "name" | "createdAt" | "updatedAt";
type SortOrder = "asc" | "desc";
type ShowFirst = "folders" | "published";

export const useFolders = () => {
  const projectId = useCurrentProjectId();
  const { data: foldersData, isLoading } = folderRouter.list.useQuery(
    projectId ? { projectId } : skipToken,
  );
  return { folders: foldersData?.folders ?? [], isLoading };
};

const getSortedElements = (
  elements: ReploElementMetadata[],
  sortOptions: {
    sortBy: SortBy;
    sortOrder: SortOrder;
    showFirst: ShowFirst[];
  },
) => {
  const { showFirst, sortBy, sortOrder } = sortOptions;

  const getSortKeyAndOrder = () => {
    const sortKey: (
      | keyof ReploElementMetadata
      | ((element: ReploElementMetadata) => string | null)
    )[] = [];
    const order: ("asc" | "desc")[] = [];

    if (showFirst.includes("published")) {
      sortKey.push((element: ReploElementMetadata) => {
        return element.isPublished ? element.publishedAt || "" : null;
      });
      order.push("asc");
    }

    if (sortBy === "name") {
      // NOTE (Fran 2024-12-12): We need to normalize the name to lowercase to sort by name correctly.
      sortKey.push((element: ReploElementMetadata) => {
        return element.name.toLowerCase();
      });
    } else {
      sortKey.push(sortBy);
    }

    order.push(sortOrder);
    return { sortKey, order };
  };

  const { sortKey, order } = getSortKeyAndOrder();

  return orderBy(elements, sortKey, order);
};

const sortFolders = (
  entries: [string, FolderWithElements][],
  moveDefaultToEnd: boolean,
) => {
  return entries.sort(([keyA], [keyB]) => {
    if (keyA === REPLO_ELEMENT_DEFAULT_FOLDER_NAME) {
      return moveDefaultToEnd ? 1 : -1;
    }
    if (keyB === REPLO_ELEMENT_DEFAULT_FOLDER_NAME) {
      return moveDefaultToEnd ? -1 : 1;
    }
    return 0;
  });
};

const useGroupedFilteredFoldersWithElementMetadata = (
  folders: Folder[],
  searchTerm: string | undefined,
  sortOptions: {
    showFirst: ShowFirst[];
    sortBy: SortBy;
    sortOrder: SortOrder;
  },
) => {
  const { data: elementMetadata } = useListAllElementsMetadata();

  const matchesSearchTerm = React.useCallback(
    (searchTarget: string | { name: string; shopifyPagePath: string }) => {
      if (!searchTerm) {
        return true;
      }

      const searchTermLower = searchTerm.toLowerCase();

      if (typeof searchTarget === "string") {
        return searchTarget.toLowerCase().includes(searchTermLower);
      }

      return (
        searchTarget.name.toLowerCase().includes(searchTermLower) ||
        searchTarget.shopifyPagePath.toLowerCase().includes(searchTermLower)
      );
    },
    [searchTerm],
  );

  return React.useMemo(() => {
    const result: GroupedFoldersWithElementMetadata = {
      page: {},
      shopifyArticle: {},
      shopifyProductTemplate: {},
      shopifySection: {},
    };

    // Note (Evan, 2024-12-11): Process folders and their elements
    for (const folder of folders) {
      const folderElements = elementMetadata.filter(
        (element) => element.folderId === folder.id,
      );

      // Note (Evan, 2024-12-11): If we're not searching or the folder's name matches the search term, include it and all its elements
      if (!searchTerm || matchesSearchTerm(folder.name)) {
        const sortedFolderElements = getSortedElements(
          folderElements,
          sortOptions,
        );

        result[folder.kind][folder.id] = {
          folder,
          elements: sortedFolderElements,
        };
        continue;
      }

      // Note (Evan, 2024-12-11): Otherwise, only include the folder if it has elements matching the search term
      const elementsMatchingSearchTerm = folderElements.filter((element) => {
        return matchesSearchTerm(element);
      });

      const sortedElementsMatchingSearchTerm = getSortedElements(
        elementsMatchingSearchTerm,
        sortOptions,
      );

      if (!isEmpty(sortedElementsMatchingSearchTerm)) {
        result[folder.kind][folder.id] = {
          folder,
          elements: sortedElementsMatchingSearchTerm,
        };
      }
    }

    // Note (Evan, 2024-12-11): Process elements not in any folder, by type
    for (const elementType of [
      "page",
      "shopifyProductTemplate",
      "shopifyArticle",
      "shopifySection",
    ] as const) {
      const elementsWithoutFolders = elementMetadata.filter(
        (element) => !element.folderId && element.type === elementType,
      );

      const filteredElementsWithoutFolders = searchTerm
        ? elementsWithoutFolders.filter((element) => matchesSearchTerm(element))
        : elementsWithoutFolders;

      const sortedElementsWithoutFolders = getSortedElements(
        filteredElementsWithoutFolders,
        sortOptions,
      );

      if (!isEmpty(sortedElementsWithoutFolders)) {
        result[elementType][REPLO_ELEMENT_DEFAULT_FOLDER_NAME] = {
          folder: undefined,
          elements: sortedElementsWithoutFolders,
        };
      }

      // NOTE (Sebas, 2025-01-20): In case showFirst includes "folders" we need to
      // move the default folder to the end of the list.
      if (sortOptions.showFirst.includes("folders")) {
        const sortedEntries = sortFolders(
          Object.entries(result[elementType]),
          true, // Move default folder to the end
        );
        result[elementType] = Object.fromEntries(sortedEntries);
      } else {
        const sortedEntries = sortFolders(
          Object.entries(result[elementType]),
          false, // Move default folder to the start
        );
        result[elementType] = Object.fromEntries(sortedEntries);
      }
    }

    return result;
  }, [elementMetadata, folders, searchTerm, matchesSearchTerm, sortOptions]);
};

type ElementsPaneContextType = {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
};

const ElementsPaneContext = React.createContext<
  ElementsPaneContextType | undefined
>(undefined);

const useElementsPaneContext = () => {
  const context = React.useContext(ElementsPaneContext);
  if (context === undefined) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      "useElementsPaneContext must be used within ElementsPaneContext",
    );
  }
  return context;
};

const ElementsPane: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState("");

  const [sortBy, setSortBy] = useLocalStorageState("replo.sort-by", "name", {
    schema: z.enum(["name", "createdAt", "updatedAt"]),
  });
  const [sortOrder, setSortOrder] = useLocalStorageState(
    "replo.sort-order",
    "asc",
    {
      schema: z.enum(["asc", "desc"]),
    },
  );
  const [showFirst, setShowFirst] = useLocalStorageState(
    "replo.show-first",
    ["folders"],
    {
      schema: z.array(z.enum(["folders", "published"])),
    },
  );

  const { elementId } = useParams();

  const logEvent = useLogAnalytics();
  const setDraftElement = useSetDraftElement();
  const { isLoading: isLoadingProject, project } = useCurrentProjectContext();
  const projectId = project?.id;
  const { isLoading: isLoadingFolders, folders } = useFolders();
  const isLoading = isLoadingProject || isLoadingFolders;
  const navigate = useNavigate();

  const groupedFoldersWithElementMetadata =
    useGroupedFilteredFoldersWithElementMetadata(folders, searchTerm, {
      showFirst,
      sortBy,
      sortOrder,
    });

  const resetDraftElement = () => {
    setDraftElement({
      componentIds: [],
    });
  };

  const resetElement = (elementId: string) => {
    resetDraftElement();

    navigate(
      generatePath(routes.editor.element, {
        projectId,
        elementId,
      }),
    );
  };

  const defaultCollapsedElementTypeGroups =
    useDefaultCollapsedElementTypeGroups(elementId);
  const localStorageCollapsedElementTypeGroupsKey = `replo.project.id.${projectId}.elements-panel.v1`;
  const [collapsedElementTypeGroups, setCollapsedElementTypeGroups] =
    useLocalStorageState(
      localStorageCollapsedElementTypeGroupsKey,
      defaultCollapsedElementTypeGroups,
      {
        schema: z.array(reploElementTypeSchema),
      },
    );

  // NOTE (Fran 2024-10-24): Every time we search, we will add a debounce to open all groups that have
  // results. We don't want to do it every time the user types because it is unnecessary and can
  // result in a performance issue.
  const collapseElementsTypeGroupAfterSearch = useDebouncedCallback(
    (value: string) => {
      if (value.length === 0) {
        setCollapsedElementTypeGroups(defaultCollapsedElementTypeGroups);
      } else {
        const collapsedElementType = new Set<ReploElementType>();

        for (const [elementType, folders] of Object.entries(
          groupedFoldersWithElementMetadata,
        )) {
          if (size(folders) > 0) {
            collapsedElementType.add(elementType as ReploElementType);
          }
        }

        setCollapsedElementTypeGroups(Array.from(collapsedElementType));
      }
    },
    300,
  );

  if (isLoading) {
    return (
      <div className="grid h-full w-full items-center justify-items-center">
        <Spinner className="size-5" variant="primary" />
      </div>
    );
  }

  return (
    <ElementsPaneContext.Provider value={{ searchTerm, setSearchTerm }}>
      <div className="flex h-full w-full flex-col gap-3 overflow-y-hidden pl-2 pt-1">
        <div className="flex gap-2 pl-1 pr-3">
          <Input
            autoComplete="off"
            placeholder="Search"
            value={searchTerm}
            layoutClassName="grow"
            onChange={(e) => {
              setSearchTerm(e.target.value);
              // NOTE (Fran 2024-10-23): We want to collapse all the element type groups after the search.
              collapseElementsTypeGroupAfterSearch(e.target.value);
            }}
            onEnter={() => {
              // NOTE (Sebas, 2024-10-18): We want to log the search event when the user
              // press enter becasuse after this the search input will be cleared.
              if (searchTerm.length > 0) {
                logEvent("element.search", {
                  searchString: searchTerm,
                });
              }
            }}
            onBlur={() => {
              // NOTE (Sebas, 2024-10-18): We want to log the search event when the user
              // press enter becasuse after this the search input will be cleared.
              if (searchTerm.length > 0) {
                logEvent("element.search", {
                  searchString: searchTerm,
                });
              }
            }}
            startEnhancer={<Search size={12} />}
          />
          <SortMenu
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            showFirst={showFirst}
            setShowFirst={(value, checked) => {
              return setShowFirst((previousValue) => {
                const newValue = new Set(previousValue);
                if (checked) {
                  newValue.add(value);
                } else {
                  newValue.delete(value);
                }
                return Array.from(newValue);
              });
            }}
          />
        </div>
        <div className="flex h-full flex-col gap-2 overflow-y-hidden">
          {ELEMENT_TYPE_GROUP_LIST.map((elementTypeGroup) => {
            const folderElementMetadataMapping =
              groupedFoldersWithElementMetadata[elementTypeGroup.type];
            const isCollapsed = collapsedElementTypeGroups.includes(
              elementTypeGroup.type,
            );
            return (
              <ElementTypeGroupContent
                key={elementTypeGroup.createButtonDataTestId}
                folderElementMetadataMapping={folderElementMetadataMapping}
                elementTypeGroup={elementTypeGroup}
                resetElement={resetElement}
                isCollapsed={isCollapsed}
                groupDataTestId={elementTypeGroup.groupDataTestId}
                setCollapsedElementTypeGroups={(
                  elementType: ReploElementType | null,
                ) => {
                  const newCollapsedElementTypeGroups = new Set(
                    collapsedElementTypeGroups,
                  );
                  if (elementType) {
                    newCollapsedElementTypeGroups.add(elementType);
                  } else {
                    newCollapsedElementTypeGroups.delete(elementTypeGroup.type);
                  }
                  setCollapsedElementTypeGroups(
                    Array.from(newCollapsedElementTypeGroups),
                  );
                }}
              />
            );
          })}
          <Separator />
        </div>
      </div>
    </ElementsPaneContext.Provider>
  );
};

const sortByOptions = [
  { label: "Alphabetical", value: "name" },
  { label: "Date Created", value: "createdAt" },
  { label: "Last Modified", value: "updatedAt" },
];

const sortOrderOptions = [
  { label: "A to Z", altLabel: "Oldest First", value: "asc" },
  { label: "Z to A", altLabel: "Newest First", value: "desc" },
];

const SortMenu: React.FC<SortMenuProps> = ({
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  showFirst,
  setShowFirst,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover
      side="right"
      shouldPreventDefaultOnInteractOutside={false}
      hideCloseButton
      style={{ marginTop: "40%" }}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      triggerAsChild
      content={
        <div className="text-default flex w-full flex-col gap-2 text-xs">
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold px-2">Sort By</h3>
            {sortByOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortBy === option.value}
                onClick={() => setSortBy(option.value as SortBy)}
                size="sm"
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold px-2">Sort Order</h3>
            {sortOrderOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortOrder === option.value}
                onClick={() => setSortOrder(option.value as SortOrder)}
                size="sm"
              >
                {sortBy === "name" ? option.label : option.altLabel}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold px-2">Group at top</h3>
            <div className="w-full px-2">
              <SwitchWithDescription
                size="sm"
                isOn={showFirst.includes("published")}
                label={<span className="text-default">Published</span>}
                onChange={(isOn) => setShowFirst("published", isOn)}
              />
            </div>
            <div className="w-full px-2">
              <SwitchWithDescription
                size="sm"
                isOn={showFirst.includes("folders")}
                label={<span className="text-default">Folders</span>}
                onChange={(isOn) => setShowFirst("folders", isOn)}
              />
            </div>
          </div>
        </div>
      }
    >
      <DeprecatedIconButton
        icon={<ListFilter size={12} />}
        tooltipText="Filter"
        variant="secondary"
        size="sm"
        isActive={isOpen}
      />
    </Popover>
  );
};

const CreateNewElementButton: React.FC<{
  elementType: ReploElementType;
  folderId?: string;
}> = ({ elementType, folderId }) => {
  const [isOpen, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();

  const createNewFolder = useCreateNewFolder();
  const { handleCreateElement } = useCreateElement();
  const dispatch = useEditorDispatch();

  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );

  const { searchTerm, setSearchTerm } = useElementsPaneContext();

  const logEvent = useLogAnalytics();

  const displayName = elementTypeToEditorData[elementType].singularDisplayName;
  const createButtonText =
    !isShopifyIntegrationEnabled && elementType !== "page"
      ? "Enabled with Shopify store integration"
      : "Create New";

  const onSelectChooseTemplate = () => {
    logEvent("editor.marketplace.browse", {
      from: "newPage",
    });

    const path = folderId
      ? `/editor/${projectId}/new?folderId=${folderId}&elementType=${elementType}`
      : `/editor/${projectId}/new?elementType=${elementType}`;

    navigate(path);
    dispatch(setLeftBarActiveTab(null));
  };

  let menuItems: MenuItemProps[] = [
    {
      type: "leaf" as const,
      id: "blank",
      variant: "default" as const,
      startEnhancer: <File size={12} className="w-[14px]" />,
      size: "sm" as const,
      title: `Blank ${displayName}`,
      onSelect: () => {
        setIsLoading(true);
        setSearchTerm("");
        void handleCreateElement({ type: elementType, folderId }).finally(
          () => {
            setIsLoading(false);
          },
        );
      },
    },
    {
      type: "leaf",
      id: "template",
      variant: "default",
      startEnhancer: <LayoutTemplate size={12} className="w-[14px]" />,
      size: "sm",
      title: "Choose Template",
      onSelect: onSelectChooseTemplate,
    },
    {
      type: "leaf",
      id: "folder",
      variant: "default" as const,
      startEnhancer: <FolderIcon size={12} className="w-[14px]" />,
      size: "sm" as const,
      title: "Folder",
      onSelect: () => {
        if (searchTerm) {
          setSearchTerm("");
        }
        logEvent("editor.folder.add");
        createNewFolder(elementType, "Untitled Folder");
      },
    },
  ];

  if (folderId) {
    menuItems = menuItems.filter(
      (item) => item.type !== "leaf" || item.id !== "folder",
    );
  }

  return (
    <Menu
      items={menuItems}
      isOpen={isOpen}
      onRequestClose={() => setOpen(false)}
      contentClassNames="mt-2"
      align="start"
      size="sm"
      trigger={
        <MenuTrigger asChild>
          {isLoading ? (
            <Spinner variant="primary" />
          ) : (
            <DeprecatedIconButton
              icon={<Plus size={16} className="text-muted" />}
              size="sm"
              tooltipText={createButtonText}
              variant="tertiary"
              onClick={() => setOpen(true)}
              disabled={!isShopifyIntegrationEnabled && elementType !== "page"}
            />
          )}
        </MenuTrigger>
      }
      disableTriggerFocusOnClose
    />
  );
};

const ElementTypeGroupContent: React.FC<
  React.PropsWithChildren<ElementTypeGroupContentProps>
> = ({
  folderElementMetadataMapping,
  elementTypeGroup,
  resetElement,
  isCollapsed,
  setCollapsedElementTypeGroups,
  groupDataTestId,
}) => {
  const { title, type, createButtonDataTestId } = elementTypeGroup;

  const hasElements = Object.values(folderElementMetadataMapping).length > 0;
  const { folders: allFolders } = useFolders();

  const hasFolders = allFolders.some((folder) => folder.kind === type);

  const hasData = hasElements || hasFolders;

  const numberOfElements = Object.values(folderElementMetadataMapping).reduce(
    (acc, { elements }) => acc + elements.length,
    0,
  );

  return (
    <div
      key={type}
      className={twMerge(
        "flex flex-col gap-2",
        // NOTE (Fran 2024-10-24): We want to make sure the element list shows at least two elements
        // if there are more than one group collapsed and one of them has several elements.
        isCollapsed && hasElements ? "min-h-16" : "min-h-8",
      )}
    >
      <div className="mr-3">
        <Separator />
      </div>
      <Group
        name={title}
        header={
          <GroupHeader
            className="text-default gap-1.5 pr-3"
            hideEndEnhancerOnGroupClosed={false}
            shouldStopPropagationOnTitleEnhancer={false}
            titleEnhancer={
              <span className="text-default flex gap-1.5 self-center whitespace-nowrap text-xs">
                <span className="text-border">|</span> {numberOfElements}
              </span>
            }
            endEnhancer={
              <div
                data-testid={`${createButtonDataTestId}-button`}
                className="h-6"
              >
                <CreateNewElementButton elementType={type} />
              </div>
            }
          >
            <GroupTitleContainer
              groupDataTestId={groupDataTestId}
              collapsibleIndicator={
                <animated.div
                  className={twMerge(
                    "hover:bg-surface text-default rounded-xs p-1",
                    !isCollapsed && "-rotate-90",
                  )}
                >
                  <ChevronDown size={12} />
                </animated.div>
              }
            />
          </GroupHeader>
        }
        isCollapsible
        isOpen={isCollapsed}
        onOpenChange={(isOpen) => {
          setCollapsedElementTypeGroups(isOpen ? type : null);
        }}
        contentClassName="styled-scrollbar pr-0.5 overflow-y-scroll"
        className="overflow-hidden"
      >
        {hasData && isCollapsed ? (
          <ElementList
            onClick={(element) => resetElement(element.id)}
            folderElementMetadataMapping={folderElementMetadataMapping}
            currentElementType={type}
          />
        ) : null}
      </Group>
    </div>
  );
};

const ElementList: React.FC<React.PropsWithChildren<ElementListProps>> = ({
  folderElementMetadataMapping,
  onClick,
  currentElementType,
}) => {
  const isDebugMode = useIsDebugMode();
  const logEvent = useLogAnalytics();
  const { isLoading } = useCurrentProjectContext();

  const {
    handleDeletion,
    handleDuplication,
    handleEditPage,
    handleMoveToFolder,
    handleSupportDuplication,
    handleArchive,
  } = usePageModificationHandlers();

  const { folders: allFolders } = useFolders();

  // NOTE (Fran 2024-10-18): We handle the loading state in the new left bar UI in other component.
  if (isLoading) {
    return null;
  }

  return (
    <ul
      className="flex flex-col gap-1 overflow-hidden"
      data-testid="elements-list"
    >
      {Object.values(folderElementMetadataMapping).map(
        ({ folder, elements }) => {
          // Note (Evan, 2024-12-05): Undefined folder means they're in no folder.
          if (folder === undefined) {
            return elements.map((element) => (
              <Menu
                key={element.id}
                items={getMenuItemsForElementItems({
                  element,
                  currentFolder: null,
                  allFolders,
                  handleDeletion,
                  handleArchive,
                  handleDuplication,
                  handleSupportDuplication,
                  handleEditPage,
                  handleMoveToFolder,
                  isDebugMode,
                })}
                menuType="context"
                customWidth="auto"
                trigger={
                  <li>
                    <ElementItem
                      element={element}
                      folder={folder}
                      onClick={(element) => {
                        onClick(element);
                        logEvent("element.select", {
                          elementId: element.id,
                          elementType: element.type,
                        });
                        saveLastSelectedElementIdForTypeForStore(
                          element.projectId,
                          element.type,
                          element.id,
                        );
                      }}
                    />
                  </li>
                }
              />
            ));
          }
          return (
            <FolderItem
              folder={folder}
              elements={elements}
              elementType={currentElementType}
              onClick={onClick}
              key={folder.id}
              startInRenameMode={
                new Date().getTime() - folder.createdAt.getTime() < 1000 &&
                folder.name !== "Support Copy"
              }
            />
          );
        },
      )}
    </ul>
  );
};

const EditingElementItemContent: React.FC<{
  elementName: string;
  setElementName: (name: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleUpdateElementName: (name: string) => void;
  setIsEditingName: (isEditing: boolean) => void;
  isInvalidElementName: boolean;
}> = ({
  elementName,
  setElementName,
  handleKeyDown,
  handleUpdateElementName,
  isInvalidElementName,
}) => {
  return (
    <Input
      autoFocus
      size="sm"
      value={elementName}
      onChange={(e) => setElementName(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        void handleUpdateElementName(elementName);
      }}
      validityState={isInvalidElementName ? "invalid" : "valid"}
    />
  );
};
const NonEditingElementItemContent: React.FC<{
  name: string;
  isCurrentElement: boolean;
  isPublished: boolean;
  isHomepage: boolean;
  type: ReploElementType;
  publishedAt?: string;
  canRenameElement: boolean;
  setIsEditingName: (isEditing: boolean) => void;
  handleOpenAnalyticsMenu: () => void;
}> = ({
  name,
  isCurrentElement,
  isPublished,
  isHomepage,
  type,
  publishedAt,
  canRenameElement,
  setIsEditingName,
  handleOpenAnalyticsMenu,
}) => {
  return (
    <>
      <span
        className={twMerge(
          "flex-1 truncate",
          isCurrentElement && "text-primary",
          !isPublished && !isCurrentElement && "text-black",
          canRenameElement && "cursor-pointer",
          !canRenameElement && "cursor-default",
        )}
        onDoubleClick={(e) => {
          e.preventDefault();
          if (canRenameElement) {
            setIsEditingName(true);
          } else {
            toast({
              header: "Cannot rename section",
              message:
                "This section cannot be renamed because it has been published.",
              type: "warning",
              cta: "Learn More",
              ctaOnClick: () => {
                window.open(docs.warnings.cannotRenameSection, "_blank");
              },
            });
          }
        }}
      >
        {name}
      </span>

      {isHomepage && (
        <Tooltip content="This page is set as the Home Page" triggerAsChild>
          <div tabIndex={0}>
            <House size={12} className="text-muted" />
          </div>
        </Tooltip>
      )}
      {isPublished && publishedAt && (
        // Note (Sebas, 2023-11-06): This margin is necessary to align the green dots
        // with the content inside the buttons (plus icon)
        <div className="mr-0.5 flex flex-row items-center gap-1">
          {type === "page" && (
            <div
              className={twMerge(
                "!h-[unset]",
                isCurrentElement
                  ? "text-primary"
                  : "text-default hidden group-hover:flex",
              )}
            >
              <DeprecatedIconButton
                icon={<ChartLine size={12} />}
                tooltipText="Open Page Analytics"
                variant="inherit"
                size="sm"
                onClick={handleOpenAnalyticsMenu}
              />
            </div>
          )}
          <Tooltip
            content={`Last Published ${formatDistanceToNowStrict(new Date(publishedAt))} ago`}
            triggerAsChild
          >
            <div tabIndex={0} className="ml-1">
              <div className="bg-emerald h-2 w-2 rounded-full" />
            </div>
          </Tooltip>
        </div>
      )}
    </>
  );
};

const ElementItem: React.FC<{
  element: ReploElementMetadata;
  folder: Folder | undefined;
  onClick(element: ReploElementMetadata): void;
}> = React.memo(function ElementItem({ element, folder, onClick }) {
  const { id, name, projectId, isPublished, type, isHomepage, publishedAt } =
    element;
  const { elementId: elementIdInUrl } = useParams();
  const isCurrentElement = id === elementIdInUrl;
  const elementHasCategory = Boolean(folder);

  const dispatch = useEditorDispatch();
  const handleClickElementItem = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      onClick(element);

      if (type !== "page" || !isPublished) {
        dispatch(setIsRightBarAnalyticsOpen(false));
      }
    }
  };

  const {
    elementName,
    setElementName,
    isEditingName,
    setIsEditingName,
    handleUpdateElementName,
    handleKeyDown,
    isInvalidElementName,
  } = useElementName({
    element,
    initialName: name,
  });

  const logEvent = useLogAnalytics();
  const handleOpenAnalyticsMenu = () => {
    dispatch(setIsRightBarAnalyticsOpen(true));
    logEvent("analytics.editor.menu.open", {});
  };

  return (
    <Link
      to={`${generatePath(routes.editor.element, {
        projectId,
        elementId: id,
      })}${location.search}`}
      className={twMerge(
        "group flex h-8 w-full cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-xs tracking-tight text-black transition duration-300",
        elementHasCategory && "pl-6",
        isCurrentElement
          ? "bg-sky-soft font-normal"
          : "hover:bg-sheet bg-white",
      )}
      onClick={handleClickElementItem}
    >
      {isEditingName ? (
        <EditingElementItemContent
          elementName={elementName}
          setElementName={setElementName}
          handleKeyDown={handleKeyDown}
          handleUpdateElementName={() => {
            return void handleUpdateElementName(elementName);
          }}
          setIsEditingName={setIsEditingName}
          isInvalidElementName={isInvalidElementName}
        />
      ) : (
        <NonEditingElementItemContent
          name={elementName}
          isCurrentElement={isCurrentElement}
          isPublished={isPublished ?? false}
          isHomepage={isHomepage}
          type={type}
          publishedAt={publishedAt}
          canRenameElement={canRenameElement(element)}
          setIsEditingName={setIsEditingName}
          handleOpenAnalyticsMenu={handleOpenAnalyticsMenu}
        />
      )}
    </Link>
  );
});

const FolderItem: React.FC<{
  folder: Folder;
  elements: ReploElementMetadata[];
  elementType: ReploElementType;
  onClick: (element: ReploElementMetadata) => void;
  startInRenameMode?: boolean;
}> = ({
  folder,
  elements,
  elementType,
  onClick,
  startInRenameMode = false,
}) => {
  const { name, id } = folder;
  const isDebugMode = useIsDebugMode();
  const { folders: allFolders } = useFolders();
  const draftElementType = useEditorSelector(selectDraftElementType);
  const draftElementId = useEditorSelector(selectDraftElementId);

  const {
    handleDeletion,
    handleDuplication,
    handleSupportDuplication,
    handleEditPage,
    handleMoveToFolder,
    handleArchive,
  } = usePageModificationHandlers();

  const { handleUpdateFolderName, handleDeleteFolder } =
    useFolderModificationHandlers();

  const [areQuickActionsOpen, setAreQuickActionsOpen] = React.useState(false);
  const [isEditingName, setIsEditingName] = React.useState(startInRenameMode);
  const [isHovered, setIsHovered] = React.useState(false);

  const thisFolderContainsTheDraftElement = Boolean(
    elements.some((element) => element.id === draftElementId),
  );
  const [isOpen, setIsOpen] = React.useState(thisFolderContainsTheDraftElement);

  useOnValueChange(
    thisFolderContainsTheDraftElement,
    (thisFolderContainsTheDraftElement) => {
      if (thisFolderContainsTheDraftElement && !isOpen) {
        setIsOpen(true);
      }
    },
  );

  const [draftName, setDraftName] = React.useState(name);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const showOnHoverStyle = useSpring({
    opacity: isHovered ? 1 : 0,
    display: isHovered ? "block" : "none",
    config: config.slow,
  });

  const hideOnHoverStyle = useSpring({
    opacity: isHovered ? 0 : 1,
    display: isHovered ? "none" : "block",
    config: config.slow,
  });

  React.useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditingName]);

  React.useEffect(() => {
    if (startInRenameMode) {
      // NOTE (Juan, 2024-03-28): When a new folder is created, we want to scroll to the top
      // of the list to show the new folder.
      const scrollableParent = inputRef.current?.closest(".styled-scrollbar");
      scrollableParent?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [startInRenameMode]);

  if (!draftElementType) {
    return null;
  }

  const hasElements = elements.length > 0;

  const menuItems: MenuItemProps[] = [
    {
      type: "leaf",
      id: "rename",
      title: "Rename Folder",
      variant: "default",
      size: "sm",
      onSelect: () => {
        setIsEditingName(true);
      },
      disabled: false,
    },
    // Note (Evan, 2024-12-06): Currently only allow deleting empty folders.
    ...(elements.length === 0
      ? [
          {
            type: "leaf" as const,
            id: "delete",
            title: "Delete Folder",
            variant: "default" as const,
            size: "sm" as const,
            onSelect: () => handleDeleteFolder(id),
          },
        ]
      : []),
  ];

  return (
    <Group
      name={draftName}
      isDefaultOpen
      isCollapsible={hasElements}
      contentClassName="py-0 overflow-hidden"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      header={
        isEditingName ? (
          <div className="flex h-8 flex-row items-center gap-2 pl-2">
            <div>
              <FolderIcon size={12} className="grow" />
            </div>
            <div className="w-full pl-1 pr-2">
              <Input
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onBlur={() => {
                  handleUpdateFolderName(id, draftName);
                  setIsEditingName(false);
                }}
                onEnter={() => {
                  handleUpdateFolderName(id, draftName);
                  setIsEditingName(false);
                }}
                ref={inputRef}
              />
            </div>
          </div>
        ) : (
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <GroupHeader
              className="group/folder-header sticky top-0 z-10 w-full bg-white pr-px"
              contentClassName="min-w-0 overflow-hidden"
              hideEndEnhancerOnGroupClosed={false}
              startEnhancer={
                <animated.div
                  className={twMerge("p-0.5", !hasElements && "block")}
                  style={hasElements ? hideOnHoverStyle : undefined}
                >
                  <FolderIcon size={12} className="shrink-0" />
                </animated.div>
              }
              endEnhancer={
                <div
                  className={twMerge(
                    // Note (Kevin, 2025-04-01): Default is hidden. Use opacity to avoid layout shifts affecting menu positioning
                    "flex shrink-0 gap-0 transition-opacity",
                    // Note (Evan, 2024-12-09): We have to do this dance because `hidden` or width changes break Radix's menu positioning.
                    "pointer-events-none opacity-0",
                    "group-hover/folder-header:pointer-events-auto group-hover/folder-header:opacity-100",
                    // Note (Evan, 2024-12-09): We always want the end enhancer to be visible when the menu is open
                    areQuickActionsOpen && "pointer-events-auto opacity-100",
                  )}
                >
                  <Menu
                    items={menuItems}
                    isOpen={areQuickActionsOpen}
                    onRequestClose={() => setAreQuickActionsOpen(false)}
                    trigger={
                      <MenuTrigger>
                        <DeprecatedIconButton
                          icon={<Ellipsis size={10} className="text-muted" />}
                          variant="tertiary"
                          size="sm"
                          layoutClassName="h-6 w-6"
                          onClick={() => setAreQuickActionsOpen(true)}
                          tooltipText="More"
                        />
                      </MenuTrigger>
                    }
                    disableTriggerFocusOnClose
                  />
                  <CreateNewElementButton
                    elementType={elementType}
                    folderId={folder.id}
                  />
                </div>
              }
            >
              <div
                className="flex h-8 w-full cursor-pointer items-center gap-1 overflow-hidden"
                onDoubleClick={() => setIsEditingName(true)}
              >
                <GroupTitleContainer
                  collapsibleIndicator={
                    <animated.div
                      style={!isOpen ? showOnHoverStyle : undefined}
                      className={twMerge(
                        "hover:bg-surface text-default rounded-xs p-1",
                        !isOpen && "-rotate-90",
                        isOpen && "block",
                      )}
                    >
                      <ChevronDown size={12} />
                    </animated.div>
                  }
                  className="truncate"
                >
                  <GroupTitle className="truncate" />
                  <span className="text-default flex gap-1.5 self-center whitespace-nowrap text-xs">
                    <span className="text-border">|</span> {elements.length}
                  </span>
                </GroupTitleContainer>
              </div>
            </GroupHeader>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-1">
        {elements.map((element) => (
          <Menu
            key={element.id}
            items={getMenuItemsForElementItems({
              element,
              currentFolder: folder,
              allFolders,
              handleDeletion,
              handleDuplication,
              handleSupportDuplication,
              handleEditPage,
              handleMoveToFolder,
              handleArchive,
              isDebugMode,
            })}
            menuType="context"
            customWidth="auto"
            trigger={
              <ElementItem
                element={element}
                folder={folder}
                onClick={(element) => {
                  saveLastSelectedElementIdForTypeForStore(
                    element.projectId,
                    element.type,
                    element.id,
                  );
                  onClick(element);
                }}
              />
            }
          />
        ))}
      </div>
    </Group>
  );
};

export function getMenuItemsForElementItems(params: {
  element: ReploElementMetadata;
  currentFolder: Folder | null;
  allFolders: Folder[];
  handleDeletion: (element: ReploElementMetadata) => void;
  handleDuplication: (element: ReploElementMetadata) => void;
  handleSupportDuplication: (element: ReploElementMetadata) => void;
  handleEditPage: (element: ReploElementMetadata) => void;
  handleArchive: (element: ReploElementMetadata) => void;
  handleMoveToFolder: (
    element: ReploElementMetadata,
    folder: Folder | null,
  ) => void;
  isDebugMode?: boolean;
  handleVersionHistory?: () => void;
}): MenuItemProps[] {
  const { element } = params;
  const menuItems: MenuItemProps[] = [
    {
      type: "leaf",
      id: "edit",
      variant: "default",
      size: "sm",
      title: `Edit ${
        elementTypeToEditorData[element.type].singularDisplayName
      } Settings`,
      onSelect: () => params.handleEditPage(element),
      disabled: false,
    },
    {
      type: "leaf",
      id: "rename",
      variant: "default",
      size: "sm",
      title: `Rename ${
        elementTypeToEditorData[element.type].singularDisplayName
      }`,
      onSelect: () => params.handleEditPage(element),
      disabled: false,
    },
    {
      type: "leaf",
      id: "duplicate",
      variant: "default",
      size: "sm",
      title: `Duplicate ${
        elementTypeToEditorData[element.type].singularDisplayName
      }`,
      onSelect: () => params.handleDuplication(element),
      disabled: false,
    },

    ...(isFeatureEnabled("archived-pages")
      ? [
          {
            type: "leaf" as const,
            id: "archive",
            variant: "default" as const,
            size: "sm" as const,
            title: `Archive ${
              elementTypeToEditorData[element.type].singularDisplayName
            }`,
            onSelect: () => params.handleArchive(element),
            disabled: false,
          },
        ]
      : [
          {
            type: "leaf" as const,
            id: "delete",
            variant: "default" as const,
            size: "sm" as const,
            title: `Delete ${
              elementTypeToEditorData[element.type].singularDisplayName
            }`,
            onSelect: () => params.handleDeletion(element),
            disabled: false,
          },
        ]),
  ];

  if (params.isDebugMode) {
    menuItems.push({
      type: "leaf",
      id: "duplicate.support",
      variant: "default",
      size: "sm",
      title: `Duplicate | Support Copy`,
      onSelect: () => params.handleSupportDuplication(element),
      disabled: false,
    });
  }

  const suitableFolders = params.allFolders.filter((folder) => {
    return (
      folder.kind === element.type && folder.id !== params.currentFolder?.id
    );
  });

  if (suitableFolders.length > 0) {
    menuItems.push({
      type: "nested",
      variant: "expandable",
      size: "sm",
      title: `Move ${
        elementTypeToEditorData[element.type].singularDisplayName
      } To Folder`,
      items: suitableFolders.map((folder) => {
        return {
          type: "leaf",
          id: folder.id,
          title: folder.name,
          variant: "default",
          size: "sm",
          onSelect: () => params.handleMoveToFolder(element, folder),
          disabled: false,
        };
      }),
    });
  }

  if (params.currentFolder) {
    menuItems.push({
      type: "leaf",
      id: "remove.from.folder",
      variant: "default",
      size: "sm",
      title: "Remove from Folder",
      onSelect: () => params.handleMoveToFolder(element, null),
      disabled: false,
    });
  }

  if (params.handleVersionHistory) {
    menuItems.push({
      items: [
        {
          type: "leaf",
          id: "version-history",
          variant: "default",
          size: "sm",
          title: `${elementTypeToEditorData[element.type].singularDisplayName} History`,
          onSelect: params.handleVersionHistory,
          disabled: false,
        },
      ],
      type: "section",
    });
  }

  return menuItems;
}

function useGetElementsByCurrentElementType() {
  const elementType = useEditorSelector(selectDraftElementType);
  const [currentElementType, _] =
    useOverridableState<ReploElementType>(elementType);
  const { data: elementsMetadata } = useListAllElementsMetadata();
  const elements = React.useMemo(() => {
    return (
      elementsMetadata?.filter(
        (element) => element.type === currentElementType,
      ) ?? []
    );
  }, [elementsMetadata, currentElementType]);

  return elements;
}

function useSearchableItems(elements: ReploElementMetadata[]) {
  const [searchTerm, _] = React.useState("");

  const filteredElements = elements.filter((element) => {
    return element.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return filteredElements;
}

export function usePageModificationHandlers() {
  const openModal = useOpenModal();
  const dispatch = useEditorDispatch();
  const elements = useGetElementsByCurrentElementType();
  const filteredElements = useSearchableItems(elements);
  const archiveElement = useArchiveElement();
  const updateElementMetadata = elementRouter.updateElementMetadata.useMutation(
    {
      onSuccess: (_, variables) => {
        // Patrick (2025-08-21) TODO: Update this to remove the type assertion whenever trpc is fixed
        // https://linear.app/replo/issue/REPL-18866/update-type-assertions-when-trpc-fixes-zod-v4-integratoin
        const { element } = variables as { element: ReploElementMetadata };
        void elementUtils.getElementMetadataById.invalidate(element.id);
        void elementUtils.listAllElementsMetadata.invalidate();
      },
    },
  );
  const logEvent = useLogAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const { elementId } = useParams();
  const { handleDuplicateElement } = useDuplicateElement();

  const handleDeletion = (element: ReploElementMetadata) => {
    openModal({
      type: "deleteElementConfirmationModal",
      props: {
        type: "delete",
        element,
        elements: filteredElements,
      },
    });
  };

  const handleDuplication = (element: ReploElementMetadata) => {
    void handleDuplicateElement(element);
  };

  const handleSupportDuplication = (element: ReploElementMetadata) => {
    void handleDuplicateElement(element, true);
  };

  const handleEditPage = (element: ReploElementMetadata) => {
    if (elementId === element.id) {
      logEvent("editor.panel.toggle", {
        toggle: "settings",
        isOpened: true,
        openedWithShortcut: false,
      });
      dispatch(setLeftBarActiveTab("settings"));
    } else {
      navigate(
        `${generatePath(routes.editor.element, {
          projectId: element.projectId,
          elementId: element.id,
        })}${location.search}`,
        {
          state: { openSettings: true },
        },
      );
    }
  };

  const handleMoveToFolder = (
    element: ReploElementMetadata,
    folder: Folder | null,
  ) => {
    updateElementMetadata.mutate({
      element: {
        ...element,
        folderId: folder?.id ?? null,
      },
    });
  };

  const handleArchive = (element: ReploElementMetadata) => {
    if (element?.isPublished) {
      openModal({
        type: "fullPageErrorModal",
        props: {
          details: {
            header: `This ${elementTypeToEditorData[element.type].singularDisplayName} is published.`,
            message: `Published content cannot be archived. Please unpublish it before archiving.`,
            callToAction: { type: "closeModal", name: "Close" },
          },
        },
      });
    } else if (element) {
      archiveElement(element, false, { from: "button" });
    }
  };

  return {
    handleDeletion,
    handleDuplication,
    handleSupportDuplication,
    handleEditPage,
    handleMoveToFolder,
    handleArchive,
  };
}

const useCreateNewFolder = () => {
  const projectId = useCurrentProjectId();
  const { mutate: createFolderMutate } = folderRouter.create.useMutation({
    onMutate: async ({ projectId, kind, name }) => {
      // Note (Evan, 2024-12-06): Cancel outgoing refetches
      await folderUtils.list.cancel();

      // Capture the previous value to return as context
      const previousFolders = folderUtils.list.getData({ projectId });

      // Optimistically update the cache
      // Note (Evan, 2024-12-06): This should be defined, this is a check for TS's sake
      if (previousFolders) {
        folderUtils.list.setData({ projectId }, () => ({
          folders: [
            { id: "new", name, kind, createdAt: new Date() },
            ...previousFolders.folders,
          ],
        }));
      }

      return { previousFolders };
    },
    onError: (_err, { projectId }, context) => {
      if (context?.previousFolders) {
        folderUtils.list.setData({ projectId }, context.previousFolders);
      }
    },
    onSettled: (_data, _error, { projectId }) => {
      void folderUtils.list.invalidate({ projectId });
    },
  });

  const createNewFolder = (kind: ReploElementType, name: string) => {
    if (projectId) {
      createFolderMutate({ projectId, kind, name });
    }
  };

  return createNewFolder;
};

const useFolderModificationHandlers = () => {
  const projectId = useCurrentProjectId();
  const { mutate: updateFolderMutate } = folderRouter.update.useMutation({
    onMutate: async ({ projectId, folderId, name }) => {
      // Note (Evan, 2024-12-06): Cancel outgoing refetches
      await folderUtils.list.cancel();

      // Capture the previous value to return as context
      const previousFolders = folderUtils.list.getData({ projectId });

      // Optimistically update the cache
      // Note (Evan, 2024-12-06): This should be defined, this is a check for TS's sake
      if (previousFolders) {
        folderUtils.list.setData({ projectId }, () => ({
          folders: previousFolders.folders.map((folder) => {
            return folder.id === folderId ? { ...folder, name } : folder;
          }),
        }));
      }

      return { previousFolders };
    },
    onError: (_err, { projectId }, context) => {
      if (context?.previousFolders) {
        folderUtils.list.setData({ projectId }, context.previousFolders);
      }
    },
    onSettled: (_data, _error, { projectId }) => {
      void folderUtils.list.invalidate({ projectId });
    },
  });

  const handleUpdateFolderName = (folderId: string, name: string) => {
    if (projectId) {
      updateFolderMutate({ projectId, folderId, name });
    }
  };

  const { mutate: deleteFolderMutate } = folderRouter.remove.useMutation({
    onMutate: async ({ projectId, folderId }) => {
      // Note (Evan, 2024-12-06): Cancel outgoing refetches
      await folderUtils.list.cancel();

      // Capture the previous value to return as context
      const previousFolders = folderUtils.list.getData({ projectId });

      // Optimistically update the cache
      // Note (Evan, 2024-12-06): This should be defined, this is a check for TS's sake
      if (previousFolders) {
        folderUtils.list.setData({ projectId }, () => ({
          folders: previousFolders.folders.filter(
            (folder) => folder.id !== folderId,
          ),
        }));
      }

      return { previousFolders };
    },
    onError: (_err, { projectId }, context) => {
      if (context?.previousFolders) {
        folderUtils.list.setData({ projectId }, context.previousFolders);
      }
    },
    onSettled: (_data, _error, { projectId }) => {
      void folderUtils.list.invalidate({ projectId });
    },
  });

  const handleDeleteFolder = (folderId: string) => {
    if (projectId) {
      deleteFolderMutate({ projectId, folderId });
    }
  };

  return {
    handleUpdateFolderName,
    handleDeleteFolder,
  };
};

export default ElementsPane;
